/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Slider,
  Select,
  SimpleGrid,
  Flex,
  Card,
  TextInput,
  // Button,
  Grid,
  Group,
  Divider,
  Modal
} from '@mantine/core';
import { IMaskInput } from 'react-imask';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import numbro from 'numbro';
import Button from '../Button';
import { GridLoader } from 'react-spinners';

const ROICalculator = () => {
  const roiMutation = useMutation({
    mutationFn: (newLead) => {
      localStorage.setItem('roiFormSubmitted', 'true');
      return axios.post('https://hook.us1.make.com/rhja6wbfhfp2trb7uep7g7uyovcl2xmk', newLead);
    }
  });

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: { name: '', email: '', phone: '' },
    validateInputOnBlur: true,
    // functions will be used to validate values at corresponding key
    validate: {
      name: (value) => {
        if (value.length === 0) {
          return 'Required';
        }
        if (value.length < 2) {
          return 'Name too short';
        }
        return null;
      },
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phone: (value) => (value.length === 0 || value.length === 14 ? null : 'Invalid phone')
    }
  });

  const [showModal, setShowModal] = useState(false);

  const [nightlyRate, setNightlyRate] = useState<number>(200);
  const [occupancyRate, setOccupancyRate] = useState<number>(70);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [numberOfCabins, setNumberOfCabins] = useState<number>(1);
  const [cabinModel, setCabinModel] = useState<string>('The Escapist');
  const [totalCost, setTotalCost] = useState<number>(0);
  const [downPayment, setDownPayment] = useState<number>(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(0);
  const [monthlyProfit, setMonthlyProfit] = useState<number>(0);

  const [isViewingMore, setIsViewingMore] = useState(false);
  const [previouslySubmittedForm] = useState(() => {
    let initialValue = false;
    // Check localStorage
    if (localStorage.getItem('roiFormSubmitted')) {
      initialValue = true;
    }

    // Check URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('skipRoiForm') === 'true') {
      initialValue = true;
    }

    return initialValue;
  });

  const formSubmitted = previouslySubmittedForm || roiMutation.isError || roiMutation.isSuccess;

  const cabinPrices: { [key: string]: number } = {
    'The Nomad': 99000,
    'The Escapist': 79000
  };

  const cabinData = [
    {
      name: 'The Escapist',
      price: 79000,
      image:
        'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e0da8edae1c46e590f56f1_Untitled_7_c0b8d81a-fc6e-46b1-8b8f-47da34702a5d.webp' // replace with actual image path
    },
    {
      name: 'The Nomad',
      price: 99000,
      image:
        'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e6a416b69a802485da868f_nomad-long.webp' // replace with actual image path
    }
  ];

  const handleSubmit = (values: typeof form.values) => {
    const cleanPhone = values?.phone?.replace(/\D/g, '');
    roiMutation.mutate({ ...values, phone: cleanPhone } as any);
    setShowModal(false);
    // setFormSubmitted(true);
  };

  const calculateFinancing = (totalCost: number): number => {
    const downPayment = totalCost * 0.2;
    setDownPayment(downPayment);
    const loanAmount = totalCost - downPayment;
    const interestRate = 0.09 / 12; // Monthly interest rate
    const numberOfPayments = 15 * 12; // 15 years
    const monthlyPayment =
      (loanAmount * interestRate * Math.pow(1 + interestRate, numberOfPayments)) /
      (Math.pow(1 + interestRate, numberOfPayments) - 1);
    return monthlyPayment;
  };

  const updateCalculations = () => {
    const cabinCost = cabinPrices[cabinModel] * numberOfCabins;
    setTotalCost(cabinCost);
    const financingPayment = calculateFinancing(cabinCost);
    setMonthlyPayment(financingPayment);
    const occupancyRateNum = occupancyRate / 100;

    const monthlyRevenueNum = nightlyRate * occupancyRateNum * 30 * numberOfCabins;
    const monthlyProfitNum = monthlyRevenueNum - financingPayment;

    setMonthlyRevenue(monthlyRevenueNum);
    setMonthlyProfit(monthlyProfitNum);
  };

  useEffect(() => {
    updateCalculations();
  }, [nightlyRate, occupancyRate, numberOfCabins, cabinModel]);

  const renderForm = () => {
    return (
      <>
        <StyledTextInput
          label="Name"
          placeholder="Name"
          key={form.key('name')}
          {...form.getInputProps('name')}
        />
        <StyledTextInput
          mt="sm"
          label="Email"
          placeholder="Email"
          key={form.key('email')}
          {...form.getInputProps('email')}
        />
        <StyledTextInput
          ta="left"
          label="Phone (optional)"
          placeholder="Your phone"
          key={form.key('phone')}
          component={IMaskInput}
          /* @ts-ignore */
          mask="(000) 000-0000"
          {...form.getInputProps('phone')}
        />
      </>
    );
  };

  return (
    <>
      <StyledContainer>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            {/* @ts-ignore */}

            <StyledInputCard shadow="sm" padding="lg" radius="md" withBorder className="input-card">
              {/* @ts-ignore */}

              <Flex direction="column" gap="lg">
                <SimpleGrid cols={2} spacing="lg">
                  {cabinData.map((cabin) => (
                    /* @ts-ignore */
                    <CabinCard
                      key={cabin.name}
                      shadow="sm"
                      padding="lg"
                      radius="md"
                      withBorder
                      selected={cabinModel === cabin.name}
                      onClick={() => setCabinModel(cabin.name)}>
                      <img
                        src={cabin.image}
                        alt={cabin.name}
                        css={{ width: '100%', borderRadius: '10px' }}
                      />
                      <Text ta="center" size="lg" mt="md">
                        {cabin.name}
                      </Text>
                    </CabinCard>
                  ))}
                </SimpleGrid>
                <InputGroup>
                  <Text>Number of Cabins:</Text>
                  <StyledSlider
                    color="black"
                    labelAlwaysOn
                    min={1}
                    max={5}
                    value={numberOfCabins}
                    onChange={setNumberOfCabins}
                    step={1}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 5, label: '5' }
                    ]}
                    label={(val) => `${val}`}
                  />
                  {/* <Text ta="center">{numberOfCabins}</Text> */}
                </InputGroup>
                <InputGroup>
                  <Text>Average Nightly Rate ($):</Text>
                  <StyledSlider
                    color="black"
                    labelAlwaysOn
                    min={80}
                    max={300}
                    value={nightlyRate}
                    onChange={setNightlyRate}
                    step={10}
                    marks={[
                      { value: 80, label: '$80' },
                      { value: 300, label: '$300' }
                    ]}
                    label={(val) => `$${val}`}
                  />
                  {/* <Text ta="center">${numbro(nightlyRate).format({ thousandSeparated: true })}</Text> */}
                </InputGroup>
                <InputGroup>
                  <Text>Occupancy Rate (%):</Text>
                  <StyledSlider
                    color="black"
                    labelAlwaysOn
                    min={25}
                    max={100}
                    value={occupancyRate}
                    onChange={setOccupancyRate}
                    step={5}
                    marks={[
                      { value: 25, label: '25%' },
                      { value: 100, label: '100%' }
                    ]}
                    label={(val) => `${val}%`}
                  />
                  {/* <Text ta="center">{numbro(occupancyRate).format({ thousandSeparated: true })}%</Text> */}
                </InputGroup>
              </Flex>
            </StyledInputCard>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <StyledDivider />
            <StyledTitle ta="center">Revenue Projections</StyledTitle>
            {/* @ts-ignore */}

            <StyledCard
              shadow="sm"
              padding="lg"
              radius="md"
              // bg="#fafafa"
              withBorder
              className="results-card">
              {/* @ts-ignore */}

              <ResultItem ta="center" mt="md">
                <ResultTitle>Monthly Revenue:</ResultTitle>
                <ResultDetail blur={!formSubmitted}>
                  ${numbro(monthlyRevenue).format({ thousandSeparated: true, mantissa: 0 })}
                </ResultDetail>
              </ResultItem>

              {/* @ts-ignore */}

              <ResultItem ta="center" mt="md">
                <ResultTitle>Est. Monthly Payment:</ResultTitle>
                <ResultDetail blur={!formSubmitted}>
                  ${numbro(monthlyPayment).format({ thousandSeparated: true, mantissa: 0 })}
                </ResultDetail>
              </ResultItem>

              {/* @ts-ignore */}
              <ResultItem ta="center" mt="md">
                <ResultTitle>Monthly Profit:</ResultTitle>
                <ResultDetail blur={!formSubmitted}>
                  ${numbro(monthlyProfit).format({ thousandSeparated: true, mantissa: 0 })}
                </ResultDetail>
              </ResultItem>
            </StyledCard>
            {!formSubmitted ? (
              <GenerateButton onClick={() => setShowModal(true)}>View Projections</GenerateButton>
            ) : (
              <>
                <Divider />
                <ResultItem ta="center" mt="md">
                  <ResultTitle>Total Cost:</ResultTitle>
                  <ResultDetail blur={!formSubmitted}>
                    ${numbro(totalCost).format({ thousandSeparated: true, mantissa: 0 })}
                  </ResultDetail>
                </ResultItem>
                {/* @ts-ignore */}
                <ResultItem ta="center" mt="md">
                  <ResultTitle>Down Payment:</ResultTitle>
                  <ResultDetail blur={!formSubmitted}>
                    ${numbro(downPayment).format({ thousandSeparated: true, mantissa: 0 })}
                  </ResultDetail>
                </ResultItem>
              </>
            )}
          </Grid.Col>
        </Grid>
        {formSubmitted && (
          <DisclaimerText>
            These revenue projections are estimates based on the input values you provide and
            average industry metrics. Actual results may vary due to market conditions, seasonality,
            and management practices. These figures do not constitute financial advice. Consult a
            financial advisor for more information.
          </DisclaimerText>
        )}
      </StyledContainer>
      <Modal
        opened={showModal}
        onClose={() => setShowModal(false)}
        title="Unlock your revenue potential"
        centered>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          {renderForm()}
          {/* @ts-ignore */}
          <ModalButton type="submit" color="#000">
            View Projections
          </ModalButton>
        </form>
      </Modal>

      {/* <MobileModal
        showModal={showModal}
        setShowModal={setShowModal}
        onButtonClick={handleFormSubmit}
        buttonText="submit">
       
      </MobileModal> */}
    </>
  );
};

export default ROICalculator;

const StyledContainer = styled(Container)`
  margin: 0;
  padding: 0px;
  width: 100% !important;
  max-width: none !important;
  padding: 0 !important;
  font-family: Satoshi, sans-serif;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  color: #000;
  font-size: 28px !important;
  margin-top: 29px !important;
`;

const DisclaimerText = styled.div`
  font-size: 12px;
  padding: 10px;
  text-align: center;
  justify-self: center;
  padding: 0 20px;
`;

const StyledCard = styled(Card)`
  background-color: #fff;
  border-radius: 0px;
  padding: 20px;
  box-shadow: none !important;
  border: none !important;
`;

const StyledInputCard = styled(StyledCard)`
  padding-left: 0;
  overflow: visible;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Results = styled.div`
  text-align: center;
`;

const ResultItem = styled<any>(Card)`
  color: #333;
  font-size: 1.1em;
  font-weight: 500;
  margin-top: 0 !important;
  box-shadow: none !important;
  background: transparent;
`;
const StyledDivider = styled(Divider)`
  @media screen and (min-width: 480px) {
    display: none;
  }
`;

const StyledSimpleGrid = styled(SimpleGrid)<any>`
  ${({ isBlurred }) =>
    isBlurred
      ? css`
          filter: blur(4px);
          position: relative;
        `
      : ''}
`;

const OverlayCard = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
`;

const StyledSlider = styled(Slider)`
  margin: 10px 0;
  .mantine-Slider-label {
    top: 0;
    height: 28px;
    line-height: 28px;
    width: 34px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 700;
    background-color: transparent;
  }

  .mantine-Slider-thumb {
    background-color: #000;
    height: 28px;
    width: 43px;
    border: none;
  }
`;

const ResultTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
`;

const ResultDetail = styled.div<any>`
  font-weight: 500;
  font-size: 20px;
  ${({ blur }) =>
    blur
      ? `
    filter: blur(5px);
  `
      : ''}
`;

const CabinCard = styled<any>(Card)`
  cursor: pointer;
  align-items: center;
  transition:
    transform 0.3s,
    box-shadow 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
  border: ${(props) =>
    props.selected ? '2px solid #1a202c !important' : '1px solid #ddd !important'};
  img {
    max-width: 180px;
  }
`;

const ViewMoreText = styled<any>(Text)`
  padding: 10px;
  cursor: pointer;
`;

const ModalButton = styled(Button)`
  width: 100%;
  max-width: none;
  padding: 16px 10px;
  font-size: 16px;
  height: auto;
  margin-top: 20px;
`;

const StyledTextInput = styled(TextInput)`
  margin-bottom: 12px;
  font-size: 16px;
  * {
    font-size: 16px;
  }
  input {
    padding: 22px 10px;
  }
`;

const GenerateButton = styled(Button)`
  max-width: 420px !important;
  margin: 0 auto;
  border-radius: 76px;
  font-size: 16px;
`;

const LoadingWrapper = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  min-height: 90px;
`;
